import type { ReactNode } from "react";

import clsx from "clsx";

import {
  AddToAListIconPNG,
  ErrorIconV2,
  resolveImageSrc,
  UserNotFoundIcon,
} from "../../Icons";
import { Typo } from "../../Typo";

type TableStateMessageProps = {
  state: "empty" | "error";
  title: string;
  description: string;
  action?: ReactNode;
  /** The standalone no-programs card: bordered, 384px tall, "User Not Found" illustration. */
  card?: boolean;
  /** Mobile sizing of the card: 16px title, 14px description, 24px above the action. */
  compact?: boolean;
};

/**
 * Table empty / error state, built to the canvas's `TableState` (`TableState.tsx`
 * + `.tablestate` in `styles.css`): a fixed 64px illustration, `typo-label-md`
 * title + `typo-body-sm` description in a centred 360px column, 24px gaps and a
 * 320px min-height. A retry action shows for `error` only — the canvas's
 * `showRetry` defaults to `state === "error"`.
 *
 * The illustration is the PNG, not `icon-add-to-a-list.svg`: the canvas points at
 * `assets/illustrations/add-to-a-list.png` ("the DS component embeds its
 * illustrations as base64 … same assets"), and every other empty state in this
 * repo renders `AddToAListIconPNG` at 64px. The SVG twin paints its frosted lens
 * with `<foreignObject>` + `backdrop-filter`, which does not render reliably.
 *
 * Not built on `Table/NoRecordsFound` even though the handoff §2 names it: that
 * component pulls `Button` from `@pepperstone/pepper-ui-kit` (excluded per
 * packages/ui/CLAUDE.md) and its CTA hard-navigates to the app root.
 */
export const TableStateMessage = ({
  state,
  title,
  description,
  action,
  card = false,
  compact = false,
}: TableStateMessageProps) => (
  <div
    className={clsx(
      "flex w-full flex-col items-center justify-center gap-6 text-center",
      card
        ? "h-96 rounded-2xl border border-stroke-primary"
        : "min-h-[320px] px-6 py-12",
    )}
    role={state === "error" ? "alert" : "status"}
    // oxlint-disable-next-line partners-custom-rule/data-testid-format -- legacy id selected by packages/e2e; never rename existing ids
    data-testid={`table-state-${state}`}
  >
    {state === "error" ? (
      <ErrorIconV2 className="size-16" aria-hidden="true" focusable="false" />
    ) : card ? (
      <div className="flex size-16 items-center justify-center">
        <UserNotFoundIcon aria-hidden="true" focusable="false" />
      </div>
    ) : (
      <img
        src={resolveImageSrc(AddToAListIconPNG)}
        alt=""
        aria-hidden="true"
        className="size-16"
      />
    )}
    <div
      className={clsx(
        "flex flex-col items-center",
        card && compact && "w-full gap-6 px-6",
        card && !compact && "max-w-[400px] gap-4",
        !card && "max-w-[360px] gap-2",
      )}
    >
      <div
        className={clsx(
          "flex flex-col items-center",
          card && compact && "w-full gap-2",
          card && !compact && "gap-4",
          !card && "gap-2",
        )}
      >
        <Typo
          element="span"
          type={card ? (compact ? "labelMD" : "labelLG") : "labelMD"}
          textColor="text-text-primary"
          className={card ? "whitespace-nowrap" : undefined}
        >
          {title}
        </Typo>
        <Typo
          element="span"
          type={card && !compact ? "bodyMD" : "bodySM"}
          textColor="text-text-secondary"
        >
          {description}
        </Typo>
      </div>
      {card ? action : null}
    </div>
    {card ? null : action}
  </div>
);
